/**
 * Sprite-Sheet-Player (BATTLE-24, Hybrid): generierte Charakter-Sheets
 * (capybara-mcp, lokal gespiegelt unter public/sprites/) als Drop-in fuer
 * die prozeduralen Species-Painter. Reiner Render-Layer (Regel 5).
 *
 * Sheets: horizontal, 400x400 px/Frame; idle 8 / walk 12 / attack 16 / death 16.
 * Laden ist lazy — bis ein Sheet bereit ist, malt der Renderer den Painter
 * (Mischbetrieb ist ausdruecklich Teil des Designs: Champs ohne Sheets
 * bleiben dauerhaft prozedural).
 */

export type SpriteAnimKind = 'idle' | 'walk' | 'attack' | 'death'

export interface SpriteAnimRef {
  kind: SpriteAnimKind
  /** Zeitbasis fuer Loops (idle/walk) — Render-Zeit, keine Sim-Zeit */
  timeMs: number
  /** 0..1 — uebersteuert die Zeitbasis (attack an pose.attackSwing, death an deathTick) */
  progress?: number
}

// Frame-Groesse wird aus der Bildhoehe abgeleitet (Frames sind quadratisch) —
// die JSON-Metadaten der Generierung (400) stimmen nicht mit den PNGs (640) ueberein!
const FRAME_MS = 55 // Anim-Tempo passend zum Schlagrhythmus (Spike-Learning)
/** Anteil Frame-Luft unter den Fuessen der Figur (400er-Frames, empirisch) */
const FOOT_PAD = 0.12

interface SheetDef {
  url: string
  frames: number
}

function sheetSet(veg: string): Record<SpriteAnimKind, SheetDef> {
  return {
    idle: { url: `/sprites/${veg}_idle.png`, frames: 8 },
    walk: { url: `/sprites/${veg}_walk.png`, frames: 12 },
    attack: { url: `/sprites/${veg}_attack.png`, frames: 16 },
    death: { url: `/sprites/${veg}_death.png`, frames: 16 },
  }
}

/** Game-Champ-Key -> Sheet-Set (docs/12-Spezies-Zuordnung; Batch 1 = Stufe-1-Roster, Batch 2 = Content-Batch-1-Champs) */
const SHEETS: Record<string, Record<SpriteAnimKind, SheetDef>> = {
  ironguard: sheetSet('kohlrabi'),
  rogue: sheetSet('aubergine'),
  musketeer: sheetSet('karotte'),
  cleric: sheetSet('knoblauch'),
  hexmaster: sheetSet('okra'),
  paladin: sheetSet('ananas'),
  mage: sheetSet('beete'),
  warlock: sheetSet('brombeere'),
  druid: sheetSet('kuerbis'),
  deathknight: sheetSet('pastinake'),
  monk: sheetSet('birne'),
  demonhunter: sheetSet('peperoni'),
  shaman: sheetSet('kokos'),
  necromancer: sheetSet('rettich'),
  tinker: sheetSet('walnuss'),
}

/**
 * Fuss-Anker pro Champ: gemessene Luft unter der Alpha-BBox (Frame 0 des idle-Sheets,
 * inkl. eingebackenem Drop-Shadow) — variiert stark (okra 0.125 … walnuss 0.256), ein
 * globaler Wert laesst einzelne Figuren schweben (PO-Befund am Walnuss-Screenshot).
 * Frame-Unterkante wird so gelegt, dass die BBox-Unterkante auf footY sitzt.
 */
const FOOT_PADS: Record<string, number> = {
  ironguard: 0.131, rogue: 0.144, musketeer: 0.15, cleric: 0.15, hexmaster: 0.125,
  paladin: 0.194, mage: 0.141, warlock: 0.134, druid: 0.147, deathknight: 0.15,
  monk: 0.162, demonhunter: 0.188, shaman: 0.2, necromancer: 0.162, tinker: 0.256,
}

const imgCache = new Map<string, HTMLImageElement>()

function getImg(url: string): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null // Node/Tests: kein DOM
  let im = imgCache.get(url)
  if (!im) {
    im = new Image()
    im.src = url
    imgCache.set(url, im)
  }
  return im
}

/** Sind ALLE Sheets dieses Champs geladen? (sonst Painter-Fallback — kein Mix pro Unit) */
export function spriteReady(champ: string): boolean {
  const set = SHEETS[champ]
  if (!set) return false
  for (const def of Object.values(set)) {
    const im = getImg(def.url)
    if (!im || !im.complete || im.naturalWidth === 0) return false
  }
  return true
}

/** Preload anstossen (fire-and-forget) — z.B. beim Renderer-Bau */
export function preloadSprites(): void {
  for (const set of Object.values(SHEETS)) {
    for (const def of Object.values(set)) getImg(def.url)
  }
}

/**
 * Einen Frame zeichnen. (x, footY) = Fusspunkt der Figur (Hex-Kontakt),
 * heightPx = Zeichenhoehe des Frames. flip spiegelt horizontal (Team B).
 * Rueckgabe false, wenn das Sheet (noch) nicht bereit ist.
 */
export function drawUnitSprite(
  ctx: CanvasRenderingContext2D,
  champ: string,
  anim: SpriteAnimRef,
  x: number,
  footY: number,
  heightPx: number,
  flip: boolean,
  desaturate = false,
): boolean {
  const def = SHEETS[champ]?.[anim.kind]
  if (!def) return false
  const im = getImg(def.url)
  if (!im || !im.complete || im.naturalWidth === 0) return false

  const frame = anim.progress !== undefined
    ? Math.max(0, Math.min(def.frames - 1, Math.floor(anim.progress * def.frames)))
    : Math.floor(anim.timeMs / FRAME_MS) % def.frames

  const framePx = im.naturalHeight // quadratische Frames, horizontal gereiht
  const dw = heightPx
  const dh = heightPx
  const dx = x - dw / 2
  const dy = footY + dh * (FOOT_PADS[champ] ?? FOOT_PAD) - dh

  ctx.save()
  if (desaturate) ctx.filter = 'grayscale(0.85) brightness(0.8)'
  if (flip) {
    ctx.translate(x, 0)
    ctx.scale(-1, 1)
    ctx.translate(-x, 0)
  }
  ctx.drawImage(im, frame * framePx, 0, framePx, framePx, dx, dy, dw, dh)
  ctx.restore()
  return true
}
